import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Un onglet à rendre. Générique : la fiche LOCATION et la fiche VENTE ont des
 * onglets différents mais la même barre — c'est l'appelant qui fournit ses
 * identités, ses libellés et ses icônes.
 */
export interface FicheTab<Id extends string> {
  id: Id;
  labelKey: string;
  icon: LucideIcon;
}

interface FicheTabBarProps<Id extends string> {
  tabs: FicheTab<Id>[];
  active: Id;
  onChange: (tab: Id) => void;
  /**
   * Champs à corriger par onglet — rendu en pastille. `level` = la couleur :
   * rouge dès qu'un champ obligatoire manque, orange si seuls des champs
   * mailing manquent (décision Thibaud 2026-09-15 : « les pastilles qui
   * concernent des champs oranges doivent être oranges ; s'il y a des oranges
   * et des rouges, rouge »).
   */
  alerts?: Partial<Record<Id, FicheTabAlert>>;
}

export type FicheTabAlert = { count: number; level: "required" | "mailing" };

export function FicheTabBar<Id extends string>({ tabs, active, onChange, alerts }: FicheTabBarProps<Id>) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2 mb-6" role="tablist">
      {tabs.map(({ id, labelKey, icon: Icon }) => {
        const isActive = active === id;
        const alert = alerts?.[id];
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(id)}
            className={cn(
              "inline-flex items-center gap-2 px-3.5 py-2 rounded-md border text-sm whitespace-nowrap",
              "cursor-pointer outline-none shadow-none transition-colors",
              isActive
                ? "bg-accent border-accent text-white font-semibold"
                : "bg-surface border-border text-text-secondary font-medium hover:border-border-hover hover:text-text-primary",
            )}
          >
            <Icon size={15} />
            {t(labelKey)}
            {alert && alert.count > 0 && (
              <span
                className={cn(
                  "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold tabular-nums",
                  isActive
                    ? (alert.level === "required" ? "bg-white text-danger" : "bg-white text-warning")
                    : (alert.level === "required" ? "bg-danger text-white" : "bg-warning text-white"),
                )}
                title={t("biens.ficheTabs.missingHint", { count: alert.count })}
              >
                {alert.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
